import type { CarePlan, Goal } from "@medplum/fhirtypes";
import {
  CARE_PLAN_ENTITY_ID_SYSTEM,
  CARE_PLAN_GOAL_MEMBER_ID_SYSTEM,
  CONDITION_ENTITY_ID_SYSTEM,
  getTRPCClient,
  memberKey,
} from "@repo/api";
import {
  type AddGoalInput,
  addGoalInputSchema,
  type CarePlanIdInput,
  type CreateCarePlanInput,
  carePlanIdInputSchema,
  carePlanIdSchema,
  caseIdSchema,
  conditionIdSchema,
  createCarePlanInputSchema,
  goalIdSchema,
  patientIdSchema,
  type ReviseCarePlanInput,
  type ReviseGoalInput,
  reviseCarePlanInputSchema,
  reviseGoalInputSchema,
} from "@repo/api/schemas";
import type { CarePlanId, CaseId, ConditionId, PatientId } from "@repo/domain";
import { highWaterMark, isMemberId, type MemberId, memberIdSequence } from "@repo/domain";
import { GOAL_ACHIEVEMENT_SYSTEM, GOAL_PRIORITY_SYSTEM } from "@repo/fhir-systems";
import type { Transaction } from "@tanstack/db";
import { eq, inArray, or, toArray, useLiveQuery } from "@tanstack/react-db";
import { defineWrite } from "../actions/define-write";
import { useCollection } from "../core/hooks";
import { getDataStoreClient } from "../core/provider";
import { DomainResultError } from "../core/write-errors";
import type { CarePlanRow, GoalRow } from "../fhir/types";
import { optimisticCaseReference } from "./_case-reference";
import { optimisticPatientReference, PATIENT_COLLECTION, patientDomainId } from "./_patient-reference";
import { visitDomainId } from "./_visit-reference";

const CARE_PLAN_COLLECTION = "carePlan";
const GOAL_COLLECTION = "goal";

const VALID_GOAL_STATUS: ReadonlySet<string> = new Set([
  "proposed",
  "planned",
  "accepted",
  "active",
  "on-hold",
  "completed",
  "cancelled",
  "entered-in-error",
  "rejected",
]);

type GoalLifecycleStatus =
  | "proposed"
  | "planned"
  | "accepted"
  | "active"
  | "on-hold"
  | "completed"
  | "cancelled"
  | "entered-in-error"
  | "rejected";

/** `none` is the DTO representation of FHIR `Goal.priority` absent. */
export type GoalPriority = "none" | "high" | "medium" | "low";

/** `none` is the DTO representation of FHIR `Goal.achievementStatus` absent. */
export type GoalAchievementStatus =
  | "none"
  | "in-progress"
  | "improving"
  | "worsening"
  | "no-change"
  | "achieved"
  | "sustaining"
  | "not-achieved"
  | "no-progress"
  | "not-attainable";

/** Comparator for a quantitative target; absent ⇒ exact. */
export type GoalTargetComparator = "<" | "<=" | ">=" | ">";

export interface CarePlanPeriodDto {
  start?: string;
  end?: string;
}

/**
 * What a goal aims at: a measurable quantity, or an outcome in words.
 *
 * FLAT. The `{measure, detail: {...}}` nesting this replaced was FHIR's
 * `Goal.target.detail[x]` showing through, and it stops at the mapper now —
 * the same shape the intent takes, so nothing between the form and the
 * aggregate has to reshape it.
 */
export type GoalTarget =
  | {
      kind: "quantity";
      /** What is tracked — free text. */
      measure: string;
      /** FHIR date (YYYY-MM-DD) deadline. */
      due?: string;
      comparator?: GoalTargetComparator;
      value: number;
      unit?: string;
    }
  | { kind: "text"; measure: string; due?: string; text: string };

/**
 * CarePlan — normalized read-model.
 * Goals are joined from the Goal collection via `CarePlan.goal[]` references.
 * `activities` are flat strings extracted from `CarePlan.activity[].detail.description`.
 */
export interface CarePlanDto {
  carePlanId: CarePlanId;
  patientId: PatientId;
  episodeOfCareId?: CaseId;
  /** The visit an encounter-scoped plan belongs to. */
  visitId?: string;
  /** `case` = case treatment plan; `visit` = encounter-scoped plan. */
  kind: "case" | "visit";

  status: "draft" | "active" | "on-hold" | "revoked" | "completed" | "entered-in-error" | "unknown";
  intent: "proposal" | "plan" | "order" | "option" | "directive";

  title?: string;
  description?: string;
  /** ISO timestamp from `CarePlan.created`. */
  created?: string;
  /** Time period the plan covers — `period.start` is the plan's start date. */
  period?: CarePlanPeriodDto;
  addresses?: ConditionId[];

  activities: string[];

  goals: Array<{
    goalId: MemberId;
    description: string;
    lifecycleStatus: GoalLifecycleStatus;
    priority: GoalPriority;
    achievementStatus: GoalAchievementStatus;
    /** FHIR date (YYYY-MM-DD) — when goal pursuit begins. */
    startDate?: string;
    /** Condition ids this goal targets — a subset of the plan's addresses[]. */
    addresses?: ConditionId[];
    /** Measurable target outcome. */
    target?: GoalTarget;
  }>;
}

/** FHIR `Goal.target[0]` → DTO target. Only Quantity/string `detail[x]` are surfaced. */
function projectGoalTarget(target: Goal["target"]): GoalTarget | undefined {
  const t = target?.[0];
  if (!t) return undefined;
  const measure = t.measure?.text ?? "";
  const due = t.dueDate ? { due: t.dueDate } : {};
  if (t.detailQuantity) {
    return {
      kind: "quantity",
      measure,
      ...due,
      value: t.detailQuantity.value ?? 0,
      ...(t.detailQuantity.comparator ? { comparator: t.detailQuantity.comparator as GoalTargetComparator } : {}),
      ...(t.detailQuantity.unit ? { unit: t.detailQuantity.unit } : {}),
    };
  }
  if (typeof t.detailString === "string") return { kind: "text", measure, ...due, text: t.detailString };
  return undefined;
}

/** DTO target → FHIR `Goal.target[]`; `null`/undefined ⇒ field absent. */
function targetToFhir(t: GoalTarget | null | undefined): Goal["target"] {
  if (!t) return undefined;
  return [
    {
      measure: { text: t.measure },
      ...(t.due ? { dueDate: t.due } : {}),
      ...(t.kind === "quantity"
        ? {
            detailQuantity: {
              value: t.value,
              ...(t.comparator ? { comparator: t.comparator } : {}),
              ...(t.unit ? { unit: t.unit } : {}),
            },
          }
        : { detailString: t.text }),
    },
  ];
}

function projectGoal(g: GoalRow): CarePlanDto["goals"][number] {
  const statusRaw = g.lifecycleStatus;
  const lifecycleStatus = statusRaw && VALID_GOAL_STATUS.has(statusRaw) ? statusRaw : "proposed";
  const priorityCode = g.priority?.coding?.[0]?.code;
  const addresses = (g.addresses ?? []).flatMap((reference) =>
    reference.identifier?.system === CONDITION_ENTITY_ID_SYSTEM && reference.identifier.value
      ? [conditionIdSchema.parse(reference.identifier.value)]
      : [],
  );
  return {
    goalId: goalIdSchema.parse(g.memberId),
    description: g.description?.text ?? "",
    lifecycleStatus: lifecycleStatus as GoalLifecycleStatus,
    priority: (priorityCode ? priorityCode.replace("-priority", "") : "none") as GoalPriority,
    achievementStatus: (g.achievementStatus?.coding?.[0]?.code ?? "none") as GoalAchievementStatus,
    ...(g.startDate ? { startDate: g.startDate } : {}),
    ...(addresses.length ? { addresses } : {}),
    ...(projectGoalTarget(g.target) ? { target: projectGoalTarget(g.target) } : {}),
  };
}

function toDto(cp: CarePlanRow, matchedGoals: GoalRow[]): CarePlanDto {
  const addresses = (cp.addresses ?? []).flatMap((reference) =>
    reference.identifier?.system === CONDITION_ENTITY_ID_SYSTEM && reference.identifier.value
      ? [conditionIdSchema.parse(reference.identifier.value)]
      : [],
  );

  const activities = (cp.activity ?? []).map((a) => a.detail?.description).filter((d): d is string => !!d);
  const episodeOfCareId = cp.episodeOfCareId;
  const visitId = visitDomainId(cp.encounter);

  // A Visit CarePlan is derived from a Case plan (`basedOn`) or scoped to an
  // encounter; a Case plan has neither. Mirrors canonical `isCaseCarePlan`.
  const kind: CarePlanDto["kind"] = cp.basedOn?.length || cp.encounter ? "visit" : "case";

  return {
    carePlanId: carePlanIdSchema.parse(cp.domainId),
    patientId: patientIdSchema.parse(patientDomainId(cp.subject)),
    kind,
    ...(episodeOfCareId ? { episodeOfCareId: caseIdSchema.parse(episodeOfCareId) } : {}),
    ...(visitId ? { visitId } : {}),
    status: (cp.status ?? "unknown") as CarePlanDto["status"],
    intent: (cp.intent ?? "plan") as CarePlanDto["intent"],
    ...(cp.title ? { title: cp.title } : {}),
    ...(cp.description ? { description: cp.description } : {}),
    ...(cp.created ? { created: cp.created } : {}),
    ...(cp.period ? { period: cp.period } : {}),
    ...(addresses.length ? { addresses } : {}),
    activities,
    goals: matchedGoals.map(projectGoal),
  };
}

export interface CarePlanFilters {
  ids?: readonly CarePlanId[];
  patientId?: PatientId;
  episodeId?: CaseId;
  visitId?: string;
  kind?: CarePlanDto["kind"];
}

interface CarePlanQueryRow {
  plan: CarePlanRow;
  goals: GoalRow[];
}

function projectCarePlanQueryRow({ plan, goals }: CarePlanQueryRow): CarePlanDto | undefined {
  if (!plan.domainId) return undefined;
  // CarePlan.goal is an ordered reference array, which cannot be equality-
  // correlated by membership; this helper preserves its declared order.
  const goalsByMemberId = new Map(goals.flatMap((goal) => (goal.memberId ? [[goal.memberId, goal] as const] : [])));
  const matched = (plan.goal ?? []).flatMap((reference) => {
    const goal =
      reference.identifier?.system === CARE_PLAN_GOAL_MEMBER_ID_SYSTEM && reference.identifier.value
        ? goalsByMemberId.get(reference.identifier.value)
        : undefined;
    return goal ? [goal] : [];
  });
  return toDto(plan, matched);
}

export function useCarePlans(params: CarePlanFilters = {}): CarePlanDto[] {
  const carePlanCollection = useCollection(CARE_PLAN_COLLECTION);
  const goalCollection = useCollection(GOAL_COLLECTION);
  const patientCollection = useCollection(PATIENT_COLLECTION);
  const { ids, patientId, episodeId, visitId, kind } = params;
  const result = useLiveQuery({
    query: (q) => {
      if (ids?.length === 0) return undefined;
      let query = q
        .from({ plan: carePlanCollection })
        .join({ patient: patientCollection }, ({ plan, patient }) => eq(plan.patientId, patient.domainId), "left");
      if (ids) query = query.where(({ plan }) => inArray(plan.domainId, [...ids]));
      if (episodeId) query = query.where(({ plan }) => eq(plan.episodeOfCareId, episodeId));
      if (visitId) query = query.where(({ plan }) => eq(plan.encounter?.identifier?.value, visitId));
      if (patientId) {
        query = query.where(({ plan, patient }) => or(eq(patient?.domainId, patientId), eq(plan.patientId, patientId)));
      }
      if (kind) query = query.where(({ plan }) => eq(plan.isCaseCarePlan, kind === "case"));
      return query.select(({ plan }) => ({
        plan: { ...plan },
        goals: toArray(
          q
            .from({ goal: goalCollection })
            .where(({ goal }) => eq(goal.carePlanJoinKey, plan.domainId))
            .select(({ goal }) => ({ ...goal })),
        ),
      }));
    },
  });
  return ((result.data as CarePlanQueryRow[] | undefined) ?? []).flatMap((row) => projectCarePlanQueryRow(row) ?? []);
}

/** Single care plan lookup by id; resolves linked goals. */
export function useCarePlan(carePlanId: CarePlanId | undefined): CarePlanDto | undefined {
  const carePlanCollection = useCollection(CARE_PLAN_COLLECTION);
  const goalCollection = useCollection(GOAL_COLLECTION);
  const patientCollection = useCollection(PATIENT_COLLECTION);
  const result = useLiveQuery({
    query: (q) => {
      if (!carePlanId) return undefined;
      return q
        .from({ plan: carePlanCollection })
        .join({ patient: patientCollection }, ({ plan, patient }) => eq(plan.patientId, patient.domainId), "left")
        .where(({ plan }) => eq(plan.domainId, carePlanId))
        .select(({ plan }) => ({
          plan: { ...plan },
          goals: toArray(
            q
              .from({ goal: goalCollection })
              .where(({ goal }) => eq(goal.carePlanJoinKey, plan.domainId))
              .select(({ goal }) => ({ ...goal })),
          ),
        }))
        .findOne();
    },
  });
  const row = result.data as CarePlanQueryRow | undefined;
  return row ? projectCarePlanQueryRow(row) : undefined;
}

const carePlans = () => getDataStoreClient().getCollection<CarePlanRow, string>(CARE_PLAN_COLLECTION);
const goals = () => getDataStoreClient().getCollection<GoalRow, string>(GOAL_COLLECTION);

function carePlanRow(carePlanId: CarePlanId): { row: CarePlanRow; locator: string } {
  const row = carePlans().get(carePlanId);
  if (!row) throw new Error(`CarePlan '${carePlanId}' is not loaded`);
  return { row, locator: carePlanId };
}

function goalRow(carePlanId: CarePlanId, goalId: MemberId): { row: GoalRow; locator: string } {
  const locator = memberKey(carePlanId, goalId);
  const row = goals().get(locator);
  if (!row) throw new Error(`Goal '${goalId}' is not loaded for CarePlan '${carePlanId}'`);
  return { row, locator };
}

/** Intent: "create a care plan for a case" */
export const createCarePlan = defineWrite<CreateCarePlanInput>({
  optimistic: (params) => {
    const input = createCarePlanInputSchema.parse(params);
    const optimisticCarePlanId = input.id;
    carePlans().insert({
      resourceType: "CarePlan",
      identifier: [{ system: CARE_PLAN_ENTITY_ID_SYSTEM, value: optimisticCarePlanId }],
      domainId: optimisticCarePlanId,
      status: "active",
      intent: "plan",
      subject: optimisticPatientReference(input.patient.id),
      patientId: input.patient.id,
      supportingInfo: [optimisticCaseReference(input.episodeOfCare.id)],
      episodeOfCareId: input.episodeOfCare.id,
      isCaseCarePlan: true,
      ...(input.title ? { title: input.title } : {}),
      ...(input.description ? { description: input.description } : {}),
    });
  },
  persist: async (params, options) => {
    const result = await getTRPCClient().carePlan.create.mutate(params, options);
    if (!result.success) throw new DomainResultError(result.error.message);
  },
});

/** Intent: "revise a care plan's title, description, or addressed conditions" */
export const reviseCarePlan = defineWrite<ReviseCarePlanInput>({
  optimistic: (params) => {
    const input = reviseCarePlanInputSchema.parse(params);
    carePlans().update(carePlanRow(input.carePlanId).locator, (draft) => {
      if (input.title !== undefined) draft.title = input.title;
      if (input.description !== undefined) draft.description = input.description;
      if (input.addresses !== undefined) {
        draft.addresses = input.addresses.map((id) => ({
          identifier: { system: CONDITION_ENTITY_ID_SYSTEM, value: id },
        }));
      }
    });
  },
  persist: async (params, options) => {
    const result = await getTRPCClient().carePlan.revise.mutate(params, options);
    if (!result.success) throw new DomainResultError(result.error.message);
  },
});

function transition(intent: "hold" | "revoke" | "complete", status: CarePlan["status"]) {
  return defineWrite<CarePlanIdInput>({
    optimistic: (params) => {
      const input = carePlanIdInputSchema.parse(params);
      carePlans().update(carePlanRow(input.carePlanId).locator, (draft) => {
        draft.status = status;
        if (status === "completed" || status === "revoked")
          draft.period = { ...draft.period, end: new Date().toISOString() };
      });
    },
    persist: async (params, options) => {
      const result = await getTRPCClient().carePlan[intent].mutate(params, options);
      if (!result.success) throw new DomainResultError(result.error.message);
    },
  });
}

/** Intent: "activate a care plan (move from draft to active)" */

/** Intent: "place a care plan on hold" */
export const holdCarePlan = transition("hold", "on-hold");

/** Intent: "revoke a care plan" */
export const revokeCarePlan = transition("revoke", "revoked");

/** Intent: "complete a care plan" */
export const completeCarePlan = transition("complete", "completed");

/** The intent's priority → FHIR `Goal.priority`; absent or `null` ⇒ not ranked. */
function priorityToFhir(p: AddGoalInput["priority"] | null): Goal["priority"] {
  return p ? { coding: [{ system: GOAL_PRIORITY_SYSTEM, code: p }] } : undefined;
}

/** GoalAchievementStatus → FHIR `Goal.achievementStatus`; `none`/undefined ⇒ field absent. */
function achievementToFhir(a: GoalAchievementStatus | undefined): Goal["achievementStatus"] {
  return a && a !== "none" ? { coding: [{ system: GOAL_ACHIEVEMENT_SYSTEM, code: a }] } : undefined;
}

/** Condition ids → FHIR `Goal.addresses` references. */
function addressesToFhir(ids: ConditionId[] | undefined): Goal["addresses"] {
  return ids?.length ? ids.map((id) => ({ identifier: { system: CONDITION_ENTITY_ID_SYSTEM, value: id } })) : undefined;
}

/** Intent: "add a goal to a care plan" */
export const addGoal = defineWrite<AddGoalInput>({
  optimistic: (params) => {
    const input = addGoalInputSchema.parse(params);
    const { row: plan, locator: planLocator } = carePlanRow(input.carePlanId);
    // Goals are never removed, so the plan's members are its high-water mark, as on the server.
    const members = [
      ...goals()
        .toArray.filter((goal) => goal.carePlanId === input.carePlanId)
        .map((goal) => goal.memberId),
      ...(plan.goal ?? []).map((reference) => reference.identifier?.value),
    ].flatMap((id) => (id && isMemberId(id) ? [{ id }] : []));
    const optimisticGoalId = memberIdSequence("goal", highWaterMark(members)).next();
    const optimisticGoal: GoalRow = {
      resourceType: "Goal",
      identifier: [
        { system: CARE_PLAN_GOAL_MEMBER_ID_SYSTEM, value: optimisticGoalId },
        { system: CARE_PLAN_ENTITY_ID_SYSTEM, value: input.carePlanId },
      ],
      memberId: optimisticGoalId,
      carePlanId: input.carePlanId,
      carePlanJoinKey: input.carePlanId,
      // Mirrors the mapper: a goal is `active` until it is retired, and a new
      // one is in progress until progress is observed otherwise.
      lifecycleStatus: "active",
      description: { text: input.description },
      subject: { ...plan.subject },
      patientId: plan.patientId,
      priority: priorityToFhir(input.priority),
      achievementStatus: achievementToFhir("in-progress"),
      startDate: input.startDate,
      addresses: addressesToFhir(input.addresses),
      target: targetToFhir(input.target),
    };
    goals().insert(optimisticGoal);
    carePlans().update(planLocator, (draft) => {
      draft.goal = [
        ...(draft.goal ?? []),
        { identifier: { system: CARE_PLAN_GOAL_MEMBER_ID_SYSTEM, value: optimisticGoalId } },
      ];
    });
  },
  persist: async (params, options) => {
    const result = await getTRPCClient().carePlan.addGoal.mutate(params, options);
    if (!result.success) throw new DomainResultError(result.error.message);
  },
});

/** Intent: "re-word, re-rank or re-aim a goal" */
export const reviseGoal: (input: ReviseGoalInput) => Transaction = defineWrite<ReviseGoalInput>({
  optimistic: (params) => {
    const input = reviseGoalInputSchema.parse(params);
    goals().update(goalRow(input.carePlanId, input.goalId).locator, (draft) => {
      if (input.description !== undefined) draft.description = { text: input.description };
      if (input.priority !== undefined) draft.priority = priorityToFhir(input.priority);
      if (input.startDate !== undefined) draft.startDate = input.startDate;
      if (input.addresses !== undefined) draft.addresses = addressesToFhir(input.addresses);
      if (input.target !== undefined) draft.target = targetToFhir(input.target);
    });
  },
  persist: async (params, options) => {
    const result = await getTRPCClient().carePlan.reviseGoal.mutate(params, options);
    if (!result.success) throw new DomainResultError(result.error.message);
  },
});
